import React from 'react'
import { useTranslation } from 'react-i18next'

// Progress bar component
export function ProgressBar({ percent, answeredCount, totalCount }) {
  const { t } = useTranslation()
  return (
    <div className="flex items-center gap-4 mb-6 p-4 bg-white rounded-xl shadow-sm">
      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-primary-500 to-primary-400 rounded-full transition-all duration-500"
          style={{ width: `${percent}%` }}
        />
      </div>
      <span className="text-sm font-semibold text-primary-600 whitespace-nowrap">
        {t('progress.percentComplete', { percent })}
      </span>
    </div>
  )
}

// Step indicator
export function StepIndicator({ current, total, sectionName }) {
  const { t } = useTranslation()
  return (
    <div className="flex items-center justify-center gap-2 mb-5 text-sm text-gray-500">
      <span className="bg-primary-500 text-white px-3 py-1 rounded-full font-semibold">
        {t('progress.stepOf', { current, total })}
      </span>
      {sectionName && <span>• {sectionName}</span>}
    </div>
  )
}

// Section header
export function SectionHeader({ number, title, subtitle }) {
  return (
    <div className="text-center mb-8">
      <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 text-white rounded-full flex items-center justify-center font-display text-xl font-semibold mx-auto mb-4 shadow-lg">
        {number}
      </div>
      <h2 className="font-display text-2xl font-medium text-gray-800 mb-3">{title}</h2>
      {subtitle && <p className="text-gray-600 max-w-xl mx-auto">{subtitle}</p>}
    </div>
  )
}

// Text input question
export function TextQuestion({ question, value, onChange, error, readOnly }) {
  return (
    <div
      className={`bg-white rounded-xl p-6 mb-4 shadow-sm transition-all border-2 ${
        error ? 'border-red-400' : 'border-transparent hover:shadow-md'
      }`}
      data-question={question.id}
    >
      <label className="block font-semibold text-gray-800 mb-3">
        {question.title}
        {question.required && !readOnly && <span className="text-red-500 ml-1">*</span>}
      </label>
      <input
        type={question.inputType || 'text'}
        className={`w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-base transition-all focus:outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-100 ${readOnly ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''}`}
        value={value || ''}
        onChange={(e) => onChange(question.id, e.target.value)}
        placeholder={question.placeholder || question.title}
        min={question.min}
        max={question.max}
        readOnly={readOnly}
        tabIndex={readOnly ? -1 : undefined}
      />
      {error && <span className="block text-red-500 text-sm mt-2 font-medium">{error}</span>}
    </div>
  )
}

// Choice/radio question
export function ChoiceQuestion({ question, value, onChange, error, readOnly }) {
  const handleSelect = (option) => {
    if (readOnly) return
    if (question.allowMultiple) {
      const current = value || []
      const newValue = current.includes(option)
        ? current.filter(v => v !== option)
        : [...current, option]
      onChange(question.id, newValue)
    } else {
      onChange(question.id, option)
    }
  }

  const isSelected = (option) => {
    if (question.allowMultiple) {
      return (value || []).includes(option)
    }
    return value === option
  }

  return (
    <div
      className={`bg-white rounded-xl p-6 mb-4 shadow-sm transition-all border-2 ${
        error ? 'border-red-400' : 'border-transparent hover:shadow-md'
      }`}
      data-question={question.id}
    >
      <label className="block font-semibold text-gray-800 mb-3">
        {question.title}
        {question.required && !readOnly && <span className="text-red-500 ml-1">*</span>}
      </label>
      <div className={`grid gap-2 ${readOnly ? 'opacity-75' : ''}`}>
        {question.options.map((option, idx) => (
          <label
            key={idx}
            className={`flex items-center gap-3 p-4 border-2 rounded-lg transition-all ${
              readOnly ? 'cursor-not-allowed' : 'cursor-pointer'
            } ${
              isSelected(option)
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'
            }`}
          >
            <input
              type={question.allowMultiple ? 'checkbox' : 'radio'}
              name={question.id}
              checked={isSelected(option)}
              onChange={() => handleSelect(option)}
              className="w-5 h-5 accent-primary-500"
              disabled={readOnly}
            />
            <span className="text-gray-700">{option}</span>
          </label>
        ))}
      </div>
      {error && <span className="block text-red-500 text-sm mt-2 font-medium">{error}</span>}
    </div>
  )
}

// Matrix/Likert scale group
export function MatrixGroup({ group, values, onChange, errors }) {
  return (
    <div className="bg-white rounded-xl p-6 shadow-sm mb-6 overflow-x-auto">
      <div className="mb-6">
        <h3 className="font-display text-xl font-medium text-gray-800">
          {group.title}
          {group.required && <span className="text-red-500 ml-1">*</span>}
        </h3>
      </div>

      <div className="min-w-[700px]">
        {/* Scale header */}
        <div className="grid gap-2 mb-3 pb-4 border-b-2 border-gray-200" 
          style={{ gridTemplateColumns: `1fr repeat(${group.responseScale.length}, 80px)` }}>
          <div></div>
          {group.responseScale.map((label, idx) => (
            <div
              key={idx}
              className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wide leading-tight select-none"
              onClick={() => {
                group.questions.forEach(q => onChange(q.id, String(idx + 1)))
              }}
            >
              {label}
            </div>
          ))}
        </div>

        {/* Questions */}
        {group.questions.map((q, qIdx) => (
          <div 
            key={q.id}
            data-question={q.id}
            className={`grid gap-2 py-4 px-2 rounded-lg items-center transition-colors ${
              errors[q.id] ? 'bg-red-50' : qIdx % 2 === 0 ? 'bg-gray-50' : ''
            } hover:bg-primary-50`}
            style={{ gridTemplateColumns: `1fr repeat(${group.responseScale.length}, 80px)` }}
          >
            <div className="text-gray-700 pr-4">
              <span className="font-bold text-primary-600 mr-2">{qIdx + 1}.</span>
              {q.title}
            </div>
            {group.responseScale.map((_, sIdx) => (
              <label key={sIdx} className="flex justify-center cursor-pointer">
                <input
                  type="radio"
                  name={q.id}
                  value={sIdx + 1}
                  checked={values[q.id] === String(sIdx + 1)}
                  onChange={() => onChange(q.id, String(sIdx + 1))}
                  className="sr-only peer"
                />
                <span className="w-6 h-6 border-2 border-gray-300 rounded-full transition-all peer-checked:border-primary-500 peer-checked:bg-primary-500 peer-checked:shadow-md hover:border-primary-400 hover:scale-110 relative">
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="w-2 h-2 bg-white rounded-full opacity-0 peer-checked:opacity-100"></span>
                  </span>
                </span>
              </label>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

// Navigation buttons
export function NavButtons({
  onPrev,
  onNext,
  showPrev = true,
  isLastStep = false,
  isSubmitting = false
}) {
  const { t } = useTranslation()
  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white py-4 px-6 shadow-[0_-4px_20px_rgba(0,0,0,0.1)] z-50">
      <div className="flex gap-4 max-w-3xl mx-auto">
        {showPrev && (
          <button
            onClick={onPrev}
            className="flex-1 py-4 px-8 bg-gray-100 text-gray-700 rounded-xl font-semibold transition-all hover:bg-gray-200"
          >
            {t('buttons.previous')}
          </button>
        )}
        <button
          onClick={onNext}
          disabled={isSubmitting}
          className="flex-1 py-4 px-8 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl font-semibold transition-all hover:shadow-lg hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {isSubmitting ? t('buttons.submitting') : isLastStep ? t('buttons.submit') : t('buttons.continueArrow')}
        </button>
      </div>
    </div>
  )
}

// Thank you screen
export function ThankYouScreen({ onReset, buttonLabel }) {
  const { t } = useTranslation()
  return (
    <div className="text-center py-16 px-6 bg-white rounded-2xl shadow-sm">
      <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-green-400 text-white rounded-full flex items-center justify-center text-4xl mx-auto mb-6 shadow-lg">
        ✓
      </div>
      <h2 className="font-display text-3xl font-medium text-gray-800 mb-4">{t('thankYou.title')}</h2>
      <p className="text-gray-600 text-lg mb-2">{t('thankYou.message')}</p>
      <p className="text-gray-500 mb-8">{t('thankYou.confidentiality')}</p>
      <button
        onClick={onReset}
        className="py-3 px-8 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl font-semibold transition-all hover:shadow-lg"
      >
        {buttonLabel || t('thankYou.submitAnother')}
      </button>
    </div>
  )
}

// Error alert
export function ErrorAlert({ message, onDismiss }) {
  if (!message) return null

  return (
    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 flex items-center justify-between">
      <span>{message}</span>
      {onDismiss && (
        <button onClick={onDismiss} className="text-red-500 hover:text-red-700 font-bold">×</button>
      )}
    </div>
  )
}
